import type { SocialPost, SocialPostStatus } from '@nucleus-commerce/api-client/admin';

import type { PostFormInput } from './post-form-schema';

/** Statuses the backend refuses to change (409 SOCIAL_POST_LOCKED); the editor opens read-only. */
export const LOCKED_STATUSES: readonly SocialPostStatus[] = ['publishing', 'published', 'removed'];

/** Statuses every list view shows unless a status filter says otherwise. */
export const LIVE_STATUSES: readonly SocialPostStatus[] = [
  'draft',
  'scheduled',
  'publishing',
  'published',
  'failed',
];

export function isPostLocked(status: SocialPostStatus): boolean {
  return LOCKED_STATUSES.includes(status);
}

/** Publish works from draft and failed (retry) and scheduled (a new time). */
export function canPublishPost(status: SocialPostStatus): boolean {
  return status === 'draft' || status === 'failed' || status === 'scheduled';
}

export type PublishBlocker = 'board' | 'media' | 'unsaved' | 'rendering';

const sameIds = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((value, i) => value === b[i]);

/**
 * What still stops the post from going out, as the backend would answer it. `unsaved` is a new
 * image list the backend has not seen; `rendering` its 2:3 rendition job, which runs after every
 * save with new images.
 */
export function publishBlockers(
  values: Pick<PostFormInput, 'target' | 'mediaIds'>,
  post: Pick<SocialPost, 'media'> | null,
  savedMediaIds: readonly string[],
): PublishBlocker[] {
  const blockers: PublishBlocker[] = [];
  if (!values.target) blockers.push('board');
  if (values.mediaIds.length === 0) blockers.push('media');
  else if (!post || !sameIds(values.mediaIds, savedMediaIds)) blockers.push('unsaved');
  else if (post.media.some((item) => values.mediaIds.includes(item.mediaId) && !item.url)) {
    blockers.push('rendering');
  }
  return blockers;
}

/** True while a saved post still waits for a rendition; the edit page polls until it is false. */
export function isRendering(post: Pick<SocialPost, 'status' | 'media'>): boolean {
  if (post.status === 'removed') return false;
  return post.media.some((item) => item.url === null);
}

/** The title a list row or a crumb shows; an untitled draft falls back to its link or id. */
export function displayPostTitle(post: Pick<SocialPost, 'title' | 'link' | 'id'>): string {
  const title = post.title?.trim();
  if (title) return title;
  return post.link ?? post.id;
}
